import { memo, useCallback, useEffect, useRef } from 'react';

import dayjs from 'dayjs';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';

import {
  parseDateRangeFilterValue,
  serializeDateRangeFilterValue,
} from '@/components/data-table/filters-v2';
import { cn } from '@/lib/utils';

import { PLANNER_DATE_RANGE_FILTER_KEY } from '../../constants';
import { PLANNER_SIDEBAR_WIDTH_PX, REM_TO_PX } from '../../constants/sizes';
import { calculateCellWidth } from '../../helpers';
import { scrollPlannerToFilterRange } from '../../helpers/scroll/scroll-to-filter-range';
import usePlannerStore from '../../store';
import { EdgeSide } from '../../types/edge-side';
import { usePlannerTooltip } from '../tooltip/planner-tooltip-provider';
import { FilterRangeBarTooltipContent } from './filter-range-bar-tooltip-content';
import { useFilterRangeBarDrag } from './hooks/use-filter-range-bar-drag';

/** hideKey identifying the bar's interactive tooltip (see InteractiveTooltipApi.show) */
const FILTER_RANGE_BAR_TOOLTIP_KEY = 'filter-range-bar';

/** The bar anchors to the sticky header root, so its left offset includes the sidebar */
const SIDEBAR_WIDTH_REM = PLANNER_SIDEBAR_WIDTH_PX / REM_TO_PX;

interface ResizeHandleProps {
  side: EdgeSide;
  onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
}

/** Invisible edge hit zone spanning the full interaction height (wider than the assignment handles for easier grabbing) */
function ResizeHandle({ side, onMouseDown }: ResizeHandleProps) {
  return (
    <div
      className={cn(
        'absolute bottom-0 top-0 z-10 w-3 cursor-ew-resize',
        side === EdgeSide.Left ? '-left-1.5' : '-right-1.5'
      )}
      onMouseDown={onMouseDown}
    />
  );
}

/**
 * The filter range bar: a solid bg-primary rectangle rendered in the
 * timeline header above the day numbers, spanning the filter date range that
 * scopes all planner filters. Thickens on hover with an interactive tooltip
 * ("Filters apply to ... " + clear), and is draggable/resizable by day like
 * assignments. Renders nothing when the filter is not set or the range lies
 * entirely outside the loaded dates window (the chip still shows the filter).
 */
export const TimelineFilterRangeBar = memo(() => {
  const dates = usePlannerStore(store => store.dates);
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  // Narrow selector: the serialized value is a referentially stable string, so
  // unrelated filter changes don't re-render the bar.
  const dateRangeValue = usePlannerStore(store => store.filters[PLANNER_DATE_RANGE_FILTER_KEY]);
  const setFilters = usePlannerStore(store => store.setFilters);
  const { interactiveTooltip } = usePlannerTooltip();

  const rawValue = Array.isArray(dateRangeValue) ? dateRangeValue[0] : dateRangeValue;
  const parsed = parseDateRangeFilterValue(rawValue);

  // TRUE (unclamped) range — the drag hook applies day shifts to these so any
  // off-screen extent survives dragging while the bar is clamped visually.
  const rangeStart = parsed ? dayjs(parsed.startDate) : null;
  const rangeEnd = parsed ? dayjs(parsed.endDate) : null;

  const handleClear = useCallback(() => {
    interactiveTooltip.hideImmediate();
    setFilters(PLANNER_DATE_RANGE_FILTER_KEY, []);
  }, [interactiveTooltip, setFilters]);

  // A drag commit changes the filter value too — it must NOT auto-center the
  // viewport (the user just placed the bar exactly where they want it).
  const skipNextAutoScrollRef = useRef(false);

  const handleCommit = useCallback(
    (newStart: dayjs.Dayjs, newEnd: dayjs.Dayjs) => {
      skipNextAutoScrollRef.current = true;
      setFilters(PLANNER_DATE_RANGE_FILTER_KEY, [
        serializeDateRangeFilterValue({ startDate: newStart.toDate(), endDate: newEnd.toDate() }),
      ]);
    },
    [setFilters]
  );

  const handleDragThresholdCrossed = useCallback(() => {
    interactiveTooltip.hideImmediate();
  }, [interactiveTooltip]);

  const {
    barRef,
    isDragging,
    isResizing,
    shouldBlockClick,
    handleBarMouseDown,
    handleResizeMouseDown,
  } = useFilterRangeBarDrag({
    startDate: rangeStart ?? dayjs(),
    endDate: rangeEnd ?? dayjs(),
    onCommit: handleCommit,
    onDragThresholdCrossed: handleDragThresholdCrossed,
    disabled: !parsed,
  });

  // Selecting a range in the filter picker navigates the planner to it
  // automatically (animated when nearby, instant when far). Skipped on mount
  // (initial load centers on today) and after drag commits.
  const previousRawValueRef = useRef(rawValue);
  useEffect(() => {
    if (rawValue === previousRawValueRef.current) return;
    previousRawValueRef.current = rawValue;

    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false;
      return;
    }

    const changedRange = parseDateRangeFilterValue(rawValue);
    if (!changedRange) return;

    scrollPlannerToFilterRange(dayjs(changedRange.startDate), dayjs(changedRange.endDate));
  }, [rawValue]);

  if (!parsed || !rangeStart || !rangeEnd || dates.length === 0) {
    return null;
  }

  const handleClick = () => {
    // Consume the click the browser fires right after a drag/resize mouseup
    if (shouldBlockClick.current) {
      shouldBlockClick.current = false;
      return;
    }
    scrollPlannerToFilterRange(rangeStart, rangeEnd);
  };

  const cellWidth = calculateCellWidth(zoomLevel);
  const baseDate = dates[0];
  const startIdx = rangeStart.startOf('day').diff(baseDate.startOf('day'), 'day');
  const endIdx = rangeEnd.startOf('day').diff(baseDate.startOf('day'), 'day');

  // Fully outside the loaded 365-day window — the chip still communicates the filter
  if (endIdx < 0 || startIdx > dates.length - 1) {
    return null;
  }

  const clampedStartIdx = Math.max(0, startIdx);
  const clampedEndIdx = Math.min(dates.length - 1, endIdx);
  const isClampedLeft = startIdx < 0;
  const isClampedRight = endIdx > dates.length - 1;

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (usePlannerStore.getState().isDragResizeInteracting) return;
    interactiveTooltip.show(
      <FilterRangeBarTooltipContent
        startDate={rangeStart}
        endDate={rangeEnd}
        onClear={handleClear}
      />,
      e.currentTarget,
      0,
      undefined,
      FILTER_RANGE_BAR_TOOLTIP_KEY
    );
  };

  const handleMouseLeave = () => {
    interactiveTooltip.hide();
  };

  const isInteracting = isDragging || isResizing !== null;

  return (
    // Outer element = generous invisible hit zone (h-5): hovering/grabbing the
    // thin bar directly is fiddly, so all pointer handlers + positioning live here.
    <div
      ref={barRef}
      className={cn('group absolute bottom-0 z-[2] h-5 cursor-grab select-none', {
        'cursor-grabbing': isInteracting,
      })}
      style={{
        left: `${SIDEBAR_WIDTH_REM + clampedStartIdx * cellWidth}rem`,
        width: `${(clampedEndIdx - clampedStartIdx + 1) * cellWidth}rem`,
      }}
      onMouseDown={handleBarMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* The visible bar; thickens while hovered anywhere in the hit zone or interacting */}
      <div
        className={cn(
          'absolute bottom-0 left-0 right-0 h-1.5 bg-primary',
          'transition-[height] duration-150 group-hover:h-3',
          isInteracting && 'h-3',
          !isClampedLeft && 'rounded-l-sm',
          !isClampedRight && 'rounded-r-sm'
        )}
      >
        {isClampedLeft && (
          <ChevronsLeft className="absolute left-0 top-1/2 h-3 w-3 -translate-y-1/2 text-primary-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
        )}
        {isClampedRight && (
          <ChevronsRight className="absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 text-primary-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
        )}
      </div>
      {!isClampedLeft && (
        <ResizeHandle
          side={EdgeSide.Left}
          onMouseDown={e => handleResizeMouseDown(EdgeSide.Left, e)}
        />
      )}
      {!isClampedRight && (
        <ResizeHandle
          side={EdgeSide.Right}
          onMouseDown={e => handleResizeMouseDown(EdgeSide.Right, e)}
        />
      )}
    </div>
  );
});
